/**
 * Typed API client for the super-admin contract template manager.
 *
 * Mirrors emailTemplates.service.ts: authService.getApi() for authenticated
 * Axios, typed interfaces, no global state.
 * Backend: app/api/v1/routes/admin_contract_templates.py.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export interface ContractTemplateVersion {
  id: string;
  version: number;
  is_published: boolean;
  published_at: string | null;
  change_notes: string | null;
  expires_after_days: number | null;
  source_mime_type: string;
  source_size_bytes: number | null;
  created_at: string;
}

export interface ContractTemplate {
  id: string;
  slug: string;
  scope: string; // 'general' | 'product'
  product_id: string | null;
  product_name: string | null;
  title: string;
  description: string | null;
  is_active: boolean;
  /** Highest published version number, or null when nothing is published. */
  live_version: number | null;
  /** active + has a published version → clients can be offered it. */
  is_offerable: boolean;
  updated_at: string;
  versions: ContractTemplateVersion[];
}

export interface SetActiveBody {
  is_active: boolean;
  reason: string;
  actor_password?: string;
}

export interface PublishVersionBody {
  reason: string;
  actor_password?: string;
}

export interface CreateTemplateBody {
  product_id: string;
  title?: string;
  description?: string;
  reason: string;
  actor_password?: string;
}

export interface UploadVersionInput {
  reason: string;
  actorPassword?: string;
  /** Without a file the generated placeholder agreement is stored. */
  file?: File | null;
  changeNotes?: string;
}

class ContractTemplatesService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  private readonly base = '/api/v1/admin/contract-templates';

  /** Every contract template with its versions. */
  async list(): Promise<ContractTemplate[]> {
    const res = await this.api.get<ContractTemplate[]>(this.base);
    return res.data;
  }

  /** The product-scoped template of a product; it has no version until one is uploaded. */
  async create(body: CreateTemplateBody): Promise<ContractTemplate> {
    const res = await this.api.post<ContractTemplate>(this.base, body);
    return res.data;
  }

  /** Add the next version, unpublished: an uploaded PDF, or the generated placeholder. */
  async uploadVersion(templateId: string, input: UploadVersionInput): Promise<ContractTemplate> {
    const form = new FormData();
    form.append('reason', input.reason);
    if (input.actorPassword) form.append('actor_password', input.actorPassword);
    if (input.changeNotes) form.append('change_notes', input.changeNotes);
    if (input.file) form.append('file', input.file);
    const res = await this.api.post<ContractTemplate>(
      `${this.base}/${templateId}/versions`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }

  /** Activate or deactivate a template (offer / withdraw lever). */
  async setActive(templateId: string, body: SetActiveBody): Promise<ContractTemplate> {
    const res = await this.api.patch<ContractTemplate>(
      `${this.base}/${templateId}/active`,
      body,
    );
    return res.data;
  }

  /**
   * Publish a version as the live one. Publish-only and forward-only
   * (SEC 17a-4 WORM): published versions are immutable; supersede by
   * publishing a higher version number.
   */
  async publishVersion(
    templateId: string,
    versionId: string,
    body: PublishVersionBody,
  ): Promise<ContractTemplate> {
    const res = await this.api.post<ContractTemplate>(
      `${this.base}/${templateId}/versions/${versionId}/publish`,
      body,
    );
    return res.data;
  }
}

export default new ContractTemplatesService();
